import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Lock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain bg-bg flex items-center justify-center px-6 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/50 px-4 py-1.5 text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold-hi)]">
            <Flame size={12} /> Admin Access
          </div>
          <h1 className="mt-6 font-serif-display text-5xl text-cream leading-tight">
            Enter the <span className="italic gold-ink">kitchen.</span>
          </h1>
          <p className="mt-3 text-cream/70 text-sm">
            Sign in to edit content and attachments.
          </p>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-warm bg-bg-card/40 backdrop-blur-xl p-8 space-y-5 shadow-gold" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] tracking-[0.35em] uppercase text-muted-warm">Email</label>
            <div className="mt-2 flex items-center gap-3 rounded-full border border-warm bg-bg px-5 py-3 focus-within:border-[color:var(--gold)] transition">
              <Mail size={16} className="text-[color:var(--gold-hi)]" />
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="bg-transparent text-cream outline-none flex-1 placeholder:text-cream/40"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.35em] uppercase text-muted-warm">Password</label>
            <div className="mt-2 flex items-center gap-3 rounded-full border border-warm bg-bg px-5 py-3 focus-within:border-[color:var(--gold)] transition">
              <Lock size={16} className="text-[color:var(--gold-hi)]" />
              <input
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="bg-transparent text-cream outline-none flex-1 placeholder:text-cream/40"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-300 bg-red-900/30 border border-red-800/50 px-4 py-3 rounded-lg" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--gold)] text-[color:var(--bg)] px-7 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[color:var(--gold-hi)] transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Flame size={16} />}
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a href="/" className="text-cream/60 hover:text-[color:var(--gold-hi)] text-xs tracking-[0.3em] uppercase" data-testid="admin-login-back">
            ← Back to Site
          </a>
        </div>
      </div>
    </div>
  );
}
